import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MeteringConceptModel } from '@bo-client/activation-management/shared/models/meteringConceptModel';
import { DocumentDtoModel } from '@bo-client/shared/models/documentDto';

import { ContactDataDto } from '@bo-client/shared/models/contactDataDto';
import {
  ProducerForm,
  ProducerModel
} from '@bo-client/activation-management/grid-registration/model/producer.model';
import {
  PVModuleForm,
  PVModuleModel
} from '@bo-client/activation-management/grid-registration/model/pv-module.model';
import {
  StorageForm,
  StorageModel
} from '@bo-client/activation-management/grid-registration/model/storage.model';
import {
  ExistingFeederForm,
  ExistingFeederFormV2,
  ExistingFeederModel
} from '@bo-client/activation-management/grid-registration/model/existing-feeder.model';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import {
  ConsumerForm,
  ConsumerFormV2,
  ConsumerModel
} from '@bo-client/activation-management/grid-registration/model/consumer.model';
import { ConnectionObjectAdditionalInfoModel } from '@bo-client/activation-management/shared/models/connection-object-additional-info.model';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import {
  FeederConnectionStandard,
  FeedingPlantTypeEnum,
  GridRegistrationTypeEnum,
  ReactivePowerProvisioningMethod,
  VoltageLevel
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { tap } from 'rxjs/operators';
import { p14aApplies } from '@bo-client/activation-management/grid-registration/grid-registration.utils';
import {
  RegexToCheckMaStrNumber,
  RegexToCheckModuleMaStrNumber
} from '@bo-client/shared/forms/forms.validators';

@Injectable({
  providedIn: 'root'
})
export class CreateGridRegistrationFormsService {
  public form = new FormGroup({}, { updateOn: 'submit' });

  constructor(
    private fb: FormBuilder,
    private createFormsService: CreateFormsService,
    private dataShareService: DataShareService
  ) {}

  createAdminMeteringConceptForm(data: MeteringConceptModel) {
    if (!data.imageDocument) {
      data.imageDocument = new DocumentDtoModel();
    }
    return this.fb.group({
      id: [data.id],
      name: [data.name],
      catalogId: [data.catalogId],
      description: [data.description],
      isFullFeedIn: [data.isFullFeedIn],
      isFullFeedOut: [data.isFullFeedOut],
      type: [data.type],
      isCommercialAccountingForwarding: [data.isCommercialAccountingForwarding],
      disabled: [data.disabled],
      sortPosition: [data.sortPosition],
      imageDocument: this.createFormsService.createUploadForm(data.imageDocument)
    });
  }

  hasInverterInstalledValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // we don't need the actual control, but insted the parent formgroup where the feedingPlantType is located aswell
      if (!control.parent) {
        return null;
      }
      control = control.parent;

      const feedingPlantTypeIsExtension =
        control.get('feedingPlantType')?.value === FeedingPlantTypeEnum.Extension;
      const hasInverterInstalledValue = control.get('hasInverterInstalled')?.value;

      console.debug(
        'hasInverterInstalledValidator',
        control,
        feedingPlantTypeIsExtension,
        hasInverterInstalledValue,
        feedingPlantTypeIsExtension && hasInverterInstalledValue == null
      );
      return feedingPlantTypeIsExtension && hasInverterInstalledValue == null
        ? {
            required: true
          }
        : null;
    };
  }

  createGridRegistrationForm(
    data: GridRegistrationModel,
    gridRegistrationType?: GridRegistrationTypeEnum,
    maxExtractionPower?: number
  ) {
    const customerDataList: ContactDataDto[] = [data.customer];
    if (data.invoiceContact && data.invoiceContact.firstName) {
      customerDataList.push(data.invoiceContact);
    }
    if (!data.connectionObjectAdditionalInfo) {
      data.connectionObjectAdditionalInfo = new ConnectionObjectAdditionalInfoModel();
    }

    if (gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer) {
      if (data.producers.length == 0) {
        const newProducer: ProducerModel = new ProducerModel();
        newProducer.count = 1;
        data.producers.push(newProducer);
      }
      if (data.pvModules.length == 0) {
        const newPvModule: PVModuleModel = new PVModuleModel();
        newPvModule.count = 1;
        data.pvModules.push(newPvModule);
      }
    }

    return this.fb.group({
      id: [data.id],
      gridRegistrationType: [data.gridRegistrationType],
      stateModifiedDate: [data.stateModifiedDate],
      technician: [data.technician],
      state: [data.state],
      powerGenerationSource: [data.powerGenerationSource],
      isEEGExtention: [data.isEEGExtention],
      feedingPlantType: [data.feedingPlantType],
      contractedConnectionPower: [data.contractedConnectionPower],
      connectionFuseAmplitude: [data.connectionFuseAmplitude],
      comment: [data.comment],
      shortDescription: [data.shortDescription],
      longDescription: [data.longDescription],
      gridConnectionPointId: [data.gridConnectionPointId],
      externalId: [data.externalId],
      externalCustomerId: [data.externalCustomerId],
      assignedTransformerStation: [data.assignedTransformerStation],
      trId: [data.trId],
      srId: [data.srId],
      meLo: [data.meLo],
      maLo: [data.maLo],
      version: [data.version],
      hasInstalledDevices: [data.hasInstalledDevices],
      hasStorage: [data.hasStorage],
      consumptionOver30kw: [data.consumptionOver30kw],
      feedInManagementInfo: [data.feedInManagementInfo],
      hasInverterInstalled: [data.hasInverterInstalled, this.hasInverterInstalledValidator()],
      meterNumber: [
        data.meterNumber,
        // tslint:disable-next-line:triple-equals
        gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer
          ? Validators.required
          : []
      ],
      placeInServiceDatum: [data.placeInService?.placeInServiceDatum],
      connectionObject: this.createFormsService.connectionObjectForm(data.connectionObject, data.uniqueCoId),
      additionalInfoConnectionObject: this.additionalInfoConnectionObjectGridRegistrationForm(
        data.connectionObjectAdditionalInfo
      ),
      extractionNetworkPowerAuthorized: [
        data.extractionNetworkPowerAuthorized,
        data.extractionNetworkPowerAuthorized && data.extractionNetworkPowerAuthorized > 0
          ? [
              Validators.required,
              this.createFormsService.greaterThanZero,
              this.createFormsService.lessThanValue(maxExtractionPower)
            ]
          : []
      ],
      filiationData: this.fb.array(
        customerDataList.map((data) => this.createFormsService.createContactDataForm(data))
      ),
      acceptConditions:
        gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer
          ? this.fb.group({
              // installationCorrectlyExecuted: ['', [Validators.required, this.mustTrue]],
              ownerConsent: ['', []]
            })
          : this.gridRegistrationConditions(),
      pvModules: this.fb.array(
        data.pvModules.map((data) => this.createPVModuleForm(data, gridRegistrationType))
      ),
      producers: this.fb.array(
        data.producers.map((prData) => this.createProducerForm(prData, gridRegistrationType))
      ),
      storages: this.fb.array(data.storages.map((data) => this.createStorageForm(data))),
      existingFeeders: this.fb.array(data.existingFeeders.map((data) => this.createExistingFeederForm(data))),
      uploads: this.fb.array(data.uploads.map((data) => this.createFormsService.createUploadForm(data))),
      consumers: this.fb.array(data.consumers.map((data) => this.createConsumerForm(data)))
    });
  }

  createPVModuleForm(data: PVModuleModel, gridRegistrationType?: GridRegistrationTypeEnum) {
    return this.fb.group({
      id: [data.id],
      manufacturer: [data.manufacturer],
      type: [
        data.type,
        gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer
          ? []
          : Validators.required
      ],
      count: [data.count, [Validators.required, this.greaterThanZero]],
      modulePowerWatt: [data.modulePowerWatt, [Validators.required, this.greaterThanZero]]
    });
  }

  createProducerForm(data: ProducerModel, gridRegistrationType?: GridRegistrationTypeEnum) {
    return this.fb.group({
      id: [data.id],
      zerezId: [data.zerezId],
      manufacturer: [
        data.manufacturer,
        gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer
          ? []
          : Validators.required
      ],
      deviceTypeName: [
        data.deviceTypeName,
        gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer
          ? []
          : Validators.required
      ],
      count: [data.count, [Validators.required, this.greaterThanZero]],
      acActivePowerMax: [
        data.acActivePowerMax,
        gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer
          ? [Validators.required, this.greaterThanZero, this.max800Watt]
          : [Validators.required, this.greaterThanZero]
      ],
      dischargePower: [data.dischargePower],
      acActivePowerMaxWatt: [
        data.acActivePowerMax * 1000,
        gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer
          ? [Validators.required, this.greaterThanZero]
          : []
      ],
      acApparentPowerMax: [data.acApparentPowerMax],
      phases: [
        data.phases,
        gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer
          ? []
          : Validators.required
      ],
      connectedPhases: [
        data.connectedPhases,
        gridRegistrationType && gridRegistrationType == GridRegistrationTypeEnum.PlugInProducer
          ? []
          : Validators.required
      ],
      maStRNumber: [data.maStRNumber],
      isExistingPowerInverter: [data.isExistingPowerInverter],
      powerInverterType: [data.powerInverterType]
    });
  }

  createStorageForm(data: StorageModel): StorageForm {
    return this.fb.group({
      id: [data.id],
      zerezId: [data.zerezId],
      zerezIdNaProtection: [data.zerezIdNaProtection],
      zerezIdPAVEProtection: [data.zerezIdPAVEProtection],
      manufacturer: [data.manufacturer],
      deviceTypeName: [data.deviceTypeName],
      usableCapacity: [data.usableCapacity],
      count: [data.count],
      couplingType: [data.couplingType],
      currentType: [data.currentType],
      disconnectionByIsolatedOperation: [data.disconnectionByIsolatedOperation],
      maStRNumber: [data.maStRNumber],
      networkAndDeviceProtection: [data.networkAndDeviceProtection],
      networkReplaceService: [data.networkReplaceService],
      phases: [data.phases],
      storageOperationType: [data.storageOperationType],
      formVersion: [data.formVersion ?? null],
      currentFlowDirectionSensor: [data.currentFlowDirectionSensor]
    });
  }

  createStorageFormV2(
    data: StorageModel,
    gridRegistration: GridRegistrationModel,
    requireZerez = true
  ): StorageForm {
    return this.fb.group({
      id: [data.id],
      zerezId: [
        data.zerezId,
        requireZerez
          ? [
              this.requiredIfConfigIsActiveAndAfter01022025AndACCoupling(gridRegistration.createdDate),
              Validators.pattern(/^ZE-[A-Z0-9]{4}-[A-Z0-9]{4}-\d{4}$/i)
            ]
          : []
      ],
      zerezIdNaProtection: [
        data.zerezIdNaProtection,
        [Validators.pattern(/^ZE-[A-Z0-9]{4}-[A-Z0-9]{4}-\d{4}$/i)]
      ],
      zerezIdPAVEProtection: [data.zerezIdPAVEProtection],
      manufacturer: [data.manufacturer],
      deviceTypeName: [data.deviceTypeName],
      usableCapacity: [data.usableCapacity],
      count: [data.count],
      couplingType: [data.couplingType, Validators.required],
      currentType: [data.currentType],
      disconnectionByIsolatedOperation: [data.disconnectionByIsolatedOperation],
      maStRNumber: [data.maStRNumber],
      networkAndDeviceProtection: [data.networkAndDeviceProtection],
      networkReplaceService: [data.networkReplaceService],
      phases: [data.phases],
      storageOperationType: [data.storageOperationType],
      formVersion: [data.formVersion ?? null],
      currentFlowDirectionSensor: [data.currentFlowDirectionSensor]
    });
  }

  createExistingFeederForm(data: ExistingFeederModel): ExistingFeederForm {
    return this.fb.group({
      id: [data.id],
      powerGenerationSource: [data.powerGenerationSource],
      power: [data.power]
    });
  }

  createExistingFeederFormV2(data: ExistingFeederModel): ExistingFeederFormV2 {
    return this.fb.group({
      id: [data.id],
      powerGenerationSource: [data.powerGenerationSource, Validators.required]
    });
  }

  createConsumerForm(data: ConsumerModel): ConsumerForm {
    return this.fb.group({
      id: [data.id],
      deviceType: [data.deviceType],
      power: [data.power, [Validators.required]],
      chargePower: [data.chargePower],
      manufacturer: [data.manufacturer],
      deviceTypeName: [data.deviceTypeName],
      count: [data.count],
      location: [data.location],
      sitePlanAvailable: [data.sitePlanAvailable],
      chargeDeviceAvailable: [data.chargeDeviceAvailable],
      chargingPointsNumber: [data.chargingPointsNumber],
      manufacturerDeclaration: [data.manufacturerDeclaration],
      extractionPowerMax: [data.extractionPowerMax],
      feedPowerMax: [data.feedPowerMax],
      chargingRangeFrom: [data.chargingRangeFrom],
      chargingRateTo: [data.chargingRateTo],
      interruptibleChargingSystem: [data.interruptibleChargingSystem],
      acCurrent: [data.acCurrent],
      chargingStationInterface: [data.chargingStationInterface],
      heatPumpType: [data.heatPumpType],
      powerDIN: [data.powerDIN],
      consumptionPower: [data.consumptionPower],
      heatingPower: [data.heatingPower],
      performancePower: [data.performancePower],
      maxConsumptionPower: [data.maxConsumptionPower],
      maxStartingCurrent: [data.maxStartingCurrent],
      hotWaterSupplyType: [data.hotWaterSupplyType],
      hotWaterSupplyPowerUsage: [data.hotWaterSupplyPowerUsage],
      roomHeatingType: [data.roomHeatingType],
      roomHeatingSupplyPowerUsage: [data.roomHeatingSupplyPowerUsage],
      operationalMode: [data.operationalMode],
      heatSource: [data.heatSource],
      switchingTime: [data.switchingTime],
      enwgP14aModule: [data.enwgP14aModule],
      rateLimitingType: [data.rateLimitingType],
      meterNumber: [data.meterNumber],
      meterOperator: [data.meterOperator],
      isP14aStvoException: [data.isP14aStvoException],
      consumerType: [data.consumerType],
      controllability: [data.controllability],
      chargerType: [data.chargerType],
      maLo: [data.maLo]
    });
  }

  createConsumerFormV2(data: ConsumerModel): ConsumerFormV2 {
    const consumerFormGroup = this.fb.group({
      id: [data.id],
      deviceType: [data.deviceType],
      power: [data.power, Validators.required],
      chargePower: [data.chargePower],
      manufacturer: [data.manufacturer, Validators.required],
      deviceTypeName: [data.deviceTypeName],
      count: [data.count, Validators.required],
      location: [data.location],
      sitePlanAvailable: [data.sitePlanAvailable],
      chargeDeviceAvailable: [data.chargeDeviceAvailable],
      chargingPointsNumber: [data.chargingPointsNumber],
      manufacturerDeclaration: [data.manufacturerDeclaration],
      extractionPowerMax: [data.extractionPowerMax],
      feedPowerMax: [data.feedPowerMax],
      chargingRangeFrom: [data.chargingRangeFrom],
      chargingRateTo: [data.chargingRateTo],
      interruptibleChargingSystem: [data.interruptibleChargingSystem],
      acCurrent: [data.acCurrent],
      chargingStationInterface: [data.chargingStationInterface],
      heatPumpType: [data.heatPumpType],
      powerDIN: [data.powerDIN],
      consumptionPower: [data.consumptionPower],
      heatingPower: [data.heatingPower],
      performancePower: [data.performancePower],
      maxConsumptionPower: [data.maxConsumptionPower],
      maxStartingCurrent: [data.maxStartingCurrent],
      hotWaterSupplyType: [data.hotWaterSupplyType],
      hotWaterSupplyPowerUsage: [data.hotWaterSupplyPowerUsage],
      roomHeatingType: [data.roomHeatingType],
      roomHeatingSupplyPowerUsage: [data.roomHeatingSupplyPowerUsage],
      operationalMode: [data.operationalMode],
      heatSource: [data.heatSource],
      switchingTime: [data.switchingTime],
      enwgP14aModule: [data.enwgP14aModule, [this.requiredIfRateLimitingType()]],
      controllability: [data.controllability, [this.requiredIfRateLimitingType()]],
      rateLimitingType: [data.rateLimitingType, this.requiredIfP14AppliesValidator()],
      enwgP14aAcceptedAGBs: [
        data.enwgP14aAcceptedAGBs,
        [this.requiredIfRateLimitingTypeAndCheckboxActivated()]
      ],
      meterNumber: [data.meterNumber],
      noMeterPresent: [data.noMeterPresent],
      meterOperator: [data.meterOperator, this.requiredIfControllabilityWmsb()],
      isP14aStvoException: [data.isP14aStvoException],
      consumerType: [data.consumerType],
      chargerType: [data.chargerType],
      maLo: [data.maLo]
    });

    // we need a little bit of custom logic the consumer form.
    consumerFormGroup.valueChanges
      .pipe(
        // in case p14 doesn't apply we want to reset the corresponding form fields.
        tap((x) => {
          if (p14aApplies(x.consumerType, x as ConsumerModel)) {
          } else {
            consumerFormGroup.get('rateLimitingType').patchValue(null, { emitEvent: false });
            consumerFormGroup.get('enwgP14aModule').patchValue(null, { emitEvent: false });
            consumerFormGroup.get('controllability').patchValue(null, { emitEvent: false });
          }
        })
      )
      .subscribe();

    return consumerFormGroup;
  }

  requiredIfP14AppliesValidator(): (control: AbstractControl) => any {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.parent || !control.parent.value) {
        return null;
      } // Sicherstellen, dass Parent und Power-Feld existieren
      const consumerModel = control.parent.value;
      if (p14aApplies(consumerModel.consumerType, consumerModel) && !control.value) {
        return { required: true };
      }
      return null;
    };
  }

  requiredIfControllabilityWmsb(): (control: AbstractControl) => any {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.parent || !control.parent.get('controllability')) {
        return null;
      } // Sicherstellen, dass Parent und Power-Feld existieren
      const controllability = control.parent.get('controllability').value;
      const isRequired = controllability === 'WMSB';
      if (isRequired && !control.value) {
        return { required: true };
      }
      return null;
    };
  }

  requiredIfRateLimitingTypeAndCheckboxActivated(): (control: AbstractControl) => any {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.parent || !control.parent.get('rateLimitingType')) {
        return null;
      } // Sicherstellen, dass Parent und Power-Feld existieren
      const rateLimtingType = control.parent.get('rateLimitingType').value;
      const isRateLimitingTypeRequired = rateLimtingType === 'EMS' || rateLimtingType === 'Direct';
      if (
        isRateLimitingTypeRequired &&
        !control.value &&
        this.dataShareService.tenantConfig.gridRegistration.enableP14AAgbCheckbox
      ) {
        return { required: true };
      }
      return null;
    };
  }

  requiredIfConfigIsActiveAndAfter01022025(
    grDateStr: Date | string | null
  ): (control: AbstractControl) => any {
    return (control: AbstractControl): { [key: string]: any } | null => {
      // Erstelle ein Datum für den 1. Februar 2025
      const targetDate = new Date('2025-02-01T00:00:00');
      const grDate = grDateStr ? new Date(grDateStr) : new Date();
      // Hole das aktuelle Datum und die Zeit

      if (!control) {
        return null;
      } // Sicherstellen, dass Parent und Power-Feld existieren

      if (
        !control.value &&
        this.dataShareService.tenantConfig.gridRegistration.zerezIdRequired &&
        grDate > targetDate
      ) {
        return { required: true };
      }
      return null;
    };
  }

  requiredIfConfigIsActiveAndAfter01022025AndACCoupling(
    grDateStr: Date | string | null
  ): (control: AbstractControl) => any {
    return (control: AbstractControl): { [key: string]: any } | null => {
      // Erstelle ein Datum für den 1. Februar 2025
      const targetDate = new Date('2025-02-01T00:00:00');
      const grDate = grDateStr ? new Date(grDateStr) : new Date();
      // Hole das aktuelle Datum und die Zeit

      if (!control) {
        return null;
      } // Sicherstellen, dass Parent und Power-Feld existieren

      if (
        !control.value &&
        this.dataShareService.tenantConfig.gridRegistration.zerezIdRequired &&
        grDate > targetDate &&
        control?.parent?.get('couplingType').value == 'AC'
      ) {
        return { required: true };
      }
      return null;
    };
  }

  requiredIfRateLimitingType(): (control: AbstractControl) => any {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.parent || !control.parent.get('rateLimitingType')) {
        return null;
      } // Sicherstellen, dass Parent und Power-Feld existieren
      const rateLimtingType = control.parent.get('rateLimitingType').value;
      const isRateLimitingTypeRequired = rateLimtingType === 'EMS' || rateLimtingType === 'Direct';
      if (isRateLimitingTypeRequired && !control.value) {
        return { required: true };
      }
      return null;
    };
  }

  /**
   * This is a simpler implementation that doesnt need to handle the differenciation in gridregtype
   * @param data
   * @param gridRegistration
   */
  createProducerFormSimple(data: ProducerModel, gridRegistration: GridRegistrationModel): ProducerForm {
    const isStorage = gridRegistration.gridRegistrationType === GridRegistrationTypeEnum.Storage;
    return this.fb.group({
      id: [data.id],
      zerezId: [
        data.zerezId,
        [
          this.requiredIfConfigIsActiveAndAfter01022025(gridRegistration.createdDate),
          Validators.pattern(/^ZE-[A-Z0-9]{4}-[A-Z0-9]{4}-\d{4}$/i)
        ]
      ],
      manufacturer: [data.manufacturer, Validators.required],
      deviceTypeName: [data.deviceTypeName, Validators.required],
      count: [data.count, [Validators.required, Validators.min(1)]],
      acActivePowerMax: [data.acActivePowerMax, [Validators.required, Validators.min(0)]],
      dischargePower: [data.dischargePower],
      acApparentPowerMax: [data.acApparentPowerMax],
      phases: [data.phases, Validators.required],
      connectedPhases: [data.connectedPhases, Validators.required],
      maStRNumber: [data.maStRNumber],
      isExistingPowerInverter: [data.isExistingPowerInverter, isStorage ? Validators.required : []],
      powerInverterType: [data.powerInverterType, isStorage ? Validators.required : []]
    });
  }

  createPVModuleFormSimple(data: PVModuleModel): PVModuleForm {
    return this.fb.group({
      id: [data.id],
      manufacturer: [data.manufacturer],
      type: [data.type, Validators.required],
      count: [data.count, [Validators.required, Validators.min(1)]],
      modulePowerWatt: [data.modulePowerWatt, [Validators.required, Validators.min(1)]]
    });
  }

  additionalInfoConnectionObjectGridRegistrationForm(data: ConnectionObjectAdditionalInfoModel) {
    return this.fb.group({
      isNewBuilding: [data.isNewBuilding],
      buildingType: [data.buildingType],
      numberOfFlats: [data.numberOfFlats],
      numberOfUsers: [data.numberOfUsers],
      numberOfOtherUsers: [data.numberOfOtherUsers],
      objectUsage: [data.objectUsage]
    });
  }

  checkIfHasProducers(listProducers: ProducerModel[]): boolean {
    if (listProducers.length > 1) {
      return true;
    }
    if (listProducers.length == 1) {
      if (listProducers[0].count > 0 && listProducers[0].acActivePowerMax > 0) {
        return true;
      }
    }
    return false;
  }

  public gridRegistrationConditions() {
    return this.fb.group({
      // installationCorrectlyExecuted: ['', [Validators.required, this.mustTrue]],
      ownerConsent: ['', [Validators.required, this.createFormsService.mustTrue]]
    });
  }

  public createMaStNumbersForm(data: GridRegistrationModel) {
    return this.fb.group({
      maStRNumberProducer: [
        data.maStRNumberProducer,
        data.producers && data.producers.length > 0
          ? [Validators.required, Validators.pattern(RegexToCheckModuleMaStrNumber)]
          : []
      ],
      maStRNumberStorage: [
        data.maStRNumberStorage,
        data.storages && data.storages.length > 0
          ? [Validators.required, Validators.pattern(RegexToCheckModuleMaStrNumber)]
          : []
      ],
      maStRNumber: [data.maStRNumber, [Validators.required, Validators.pattern(RegexToCheckMaStrNumber)]],
      maStRRegistrationDate: [data.maStRRegistrationDate, Validators.required]
    });
  }

  public createGridConnectionPointForm(data: GridRegistrationModel, maxExtractionPower: number) {
    const defaultValueGridConnectionPoint =
      data.gridConnectionPoint ||
      this.dataShareService.tenantConfig.gridRegistration.defaultGridConnectionPoint ||
      'Anschluss des Grundstückes an das Niederspannungsnetz (Ende des Netzanschlusses)';

    const defaultCosPhi =
      data.cosPhi || this.dataShareService.tenantConfig.gridRegistration.defaultCosPhi || '0,95';

    const defaultPowerProvisioningMethod =
      data.reactivePowerProvisioningMethod ||
      this.dataShareService.tenantConfig.gridRegistration.defaultReactivePowerProvisioningMethod ||
      ReactivePowerProvisioningMethod.COS_PHI_P_STANDARD_CURVE;

    return this.fb.group({
      gridConnectionPoint: [
        data.gridRegistrationType != GridRegistrationTypeEnum.ChangeMeteringConcept
          ? defaultValueGridConnectionPoint
          : null,
        data.gridRegistrationType != GridRegistrationTypeEnum.ChangeMeteringConcept ? Validators.required : []
      ],
      gridConnectionPointId: [null, []],
      feederConnectionStandard: [
        data.gridRegistrationType === GridRegistrationTypeEnum.Feeder
          ? FeederConnectionStandard.VDE4105
          : null,
        data.gridRegistrationType === GridRegistrationTypeEnum.Feeder ? Validators.required : []
      ],
      assignedTransformerStation: [data.assignedTransformerStation, []],
      reactivePowerProvisioningMethod: [
        data.gridRegistrationType === GridRegistrationTypeEnum.Feeder ? defaultPowerProvisioningMethod : null,
        data.gridRegistrationType === GridRegistrationTypeEnum.Feeder ? Validators.required : []
      ],
      cosPhi: [
        data.gridRegistrationType === GridRegistrationTypeEnum.Feeder ? defaultCosPhi : null,
        data.gridRegistrationType === GridRegistrationTypeEnum.Feeder ? Validators.required : []
      ],
      voltageLevel: [
        data.gridRegistrationType != GridRegistrationTypeEnum.ChangeMeteringConcept ? VoltageLevel.Low : null,
        data.gridRegistrationType != GridRegistrationTypeEnum.ChangeMeteringConcept ? Validators.required : []
      ],
      loadManagementRequired: [
        data.extractionNetworkPowerAuthorized && data.extractionNetworkPowerAuthorized > 0
      ],
      extractionNetworkPowerAuthorized: [
        data.extractionNetworkPowerAuthorized,
        data.extractionNetworkPowerAuthorized && data.extractionNetworkPowerAuthorized > 0
          ? [
              Validators.required,
              this.createFormsService.greaterThanZero,
              this.createFormsService.lessThanValue(maxExtractionPower)
            ]
          : []
      ],
      approvalComment: [data.approvalComment || null]
    });
  }

  public createFeedinManagementConfigurationForm(data: GridRegistrationModel) {
    return this.fb.group({
      feedInManagementSerialNumber: [data.feedInManagementSerialNumber, Validators.required]
    });
  }

  createGRFeedinOrderConfirmation() {
    return this.createFormsService.formConfirmation();
  }

  greaterThanZero(control) {
    const enteredValue = control.value;
    return !(enteredValue > 0) ? { greaterThanZero: true } : null;
  }

  max600Watt(control) {
    const enteredValue = control.value;
    return enteredValue > 0.6 ? { max600Watt: true } : null;
  }

  max800Watt(control) {
    const enteredValue = control.value;
    return enteredValue > 0.8 ? { max800Watt: true } : null;
  }
}
